import React, { useState, useEffect } from 'react';
import { useApp } from '../components/AppContext';
import { MainLayout } from '../components/MainLayout';
import { Bell, Clock } from 'lucide-react';

// INTERFACE FOR FUTURE NOTIFICATION SYSTEM
interface NotificationItem {
  id: string;
  type: 'system' | 'event' | 'payment' | 'welcome';
  title: string;
  message: string;
  time: string;
  isRead: boolean;
  actionText?: string;
  actionScreen?: string;
}

export function Notifications() {
  const { navigateDirectly } = useApp();
  
  // COMMENTED OUT FOR FUTURE USE - NOTIFICATION STATE AND DATA
  /*
  const [notifications, setNotifications] = useState<NotificationItem[]>([
    {
      id: '1',
      type: 'system',
      title: 'Contract Data Required',
      message: 'Please complete your contract data to proceed with event applications. Missing information may delay your participation.',
      time: '2 hours ago',
      isRead: false,
      actionText: 'Complete Now',
      actionScreen: 'intro-contract-data'
    },
    {
      id: '2',
      type: 'event',
      title: 'Event Application Update',
      message: 'Your application for November 15th trial operation has been approved! Please check your event details for next steps.',
      time: '1 day ago',
      isRead: false,
      actionText: 'View Event Details',
      actionScreen: 'my-event'
    },
    {
      id: '3',
      type: 'payment',
      title: 'Payment Information Needed',
      message: 'To receive your participation compensation, please complete your payroll information including banking details.',
      time: '3 days ago',
      isRead: true,
      actionText: 'Add Payment Info',
      actionScreen: 'intro-payroll-info'
    },
    {
      id: '4',
      type: 'welcome',
      title: 'Welcome to UCM Airport Inspector',
      message: 'Welcome! Complete your profile to get started with the Frankfurt Airport trial operations program.',
      time: '1 week ago',
      isRead: true
    }
  ]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'system': return '📋';
      case 'event': return '📅';
      case 'payment': return '💰';
      case 'welcome': return '👋';
      default: return '📢';
    }
  };

  const getNotificationIconBg = (type: string) => {
    switch (type) {
      case 'system': return 'bg-[var(--ucm-light-orange)]';
      case 'event': return 'bg-[var(--ucm-light-blue)]';
      case 'payment': return 'bg-[var(--ucm-light-green)]';
      case 'welcome': return 'bg-[var(--ucm-light-yellow)]';
      default: return 'bg-[var(--ucm-disabled-gray)]';
    }
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notification => ({
      ...notification,
      isRead: true
    })));
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(notification => 
      notification.id === id ? { ...notification, isRead: true } : notification
    ));
  };

  const handleNotificationAction = (notification: NotificationItem) => {
    if (notification.actionScreen) {
      markAsRead(notification.id);
      navigateDirectly(notification.actionScreen as any);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  */

  return (
    <MainLayout showBack={true} onBack={() => navigateDirectly('dashboard')}>
      <div className="bg-[var(--ucm-bg-gray)] min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-[var(--ucm-border-light)] px-5 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--ucm-text-dark)]">
              Notifications
            </h2>
            {/* MARK ALL READ BUTTON - COMMENTED OUT FOR FUTURE USE */}
            {/*
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] font-medium"
              >
                Mark all read
              </button>
            )}
            */}
          </div>
        </div>

        {/* Coming Soon Container - TEMPORARY UNTIL NOTIFICATION SYSTEM IS IMPLEMENTED */}
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="bg-white rounded-2xl p-8 shadow-[0_4px_16px_rgba(0,0,0,0.08)] max-w-md w-full text-center">
            <div className="w-16 h-16 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell size={32} className="text-[var(--ucm-dark-blue)]" />
            </div>
            
            <h2 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-2">
              Notifications Coming Soon
            </h2>
            
            <p className="text-[var(--ucm-text-medium)] mb-6">
              Wir entwickeln ein Benachrichtigungssystem, um Sie über wichtige Ereignisse und Nachrichten auf dem Laufenden zu halten. Schauen Sie bald wieder vorbei!
            </p>

            <div className="flex items-center justify-center text-sm text-[var(--ucm-text-light)]">
              <Clock size={16} className="mr-2" />
              <span>In zukünftiger Version verfügbar</span>
            </div>
          </div>
        </div>

        {/* ORIGINAL NOTIFICATION LIST UI - COMMENTED OUT FOR FUTURE USE */}
        {/*
        <div className="p-4 space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white rounded-xl p-4 border transition-all duration-200 ${
                notification.isRead 
                  ? 'border-[var(--ucm-border-light)]' 
                  : 'border-l-4 border-l-[var(--ucm-info-blue)] border-r border-t border-b border-r-[var(--ucm-border-light)] border-t-[var(--ucm-border-light)] border-b-[var(--ucm-border-light)] bg-blue-50/30'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationIconBg(notification.type)}`}>
                  <span className="text-lg">
                    {getNotificationIcon(notification.type)}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-[var(--ucm-text-dark)] text-base leading-tight">
                      {notification.title}
                    </h4>
                    <span className="text-xs text-[var(--ucm-text-light)] ml-3 flex-shrink-0">
                      {notification.time}
                    </span>
                  </div>
                  
                  <p className="text-sm text-[var(--ucm-text-medium)] leading-relaxed mb-3">
                    {notification.message}
                  </p>

                  {notification.actionText && notification.actionScreen && (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleNotificationAction(notification)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                          notification.isRead
                            ? 'bg-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] hover:bg-[var(--ucm-disabled-gray)]'
                            : 'bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)]'
                        }`}
                      >
                        {notification.actionText}
                      </button>
                    </div>
                  )}
                </div>

                {!notification.isRead && (
                  <div className="w-2 h-2 bg-[var(--ucm-info-blue)] rounded-full flex-shrink-0 mt-2">
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {notifications.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔔</div>
            <h3 className="text-lg font-semibold text-[var(--ucm-text-dark)] mb-2">
              No notifications yet
            </h3>
            <p className="text-[var(--ucm-text-medium)]">
              You'll receive important updates and messages here.
            </p>
          </div>
        )}
        */}
      </div>
    </MainLayout>
  );
}